import React, { useReducer } from 'react';
import PropTypes from 'prop-types';

import Avatar from '@strava/ui/Avatar';
import SportsRunNormalXsmall from '@strava/icons/SportsRunNormalXsmall';
import SportsWaterNormalXsmall from '@strava/icons/SportsWaterNormalXsmall';
import SportsBikeNormalXsmall from '@strava/icons/SportsBikeNormalXsmall';
import I18n from 'utils/I18n';
import styles from './styles.scss';
import useFeedAnalytics from '../../hooks/useFeedAnalytics';
import {
  pageProps,
  viewingAthleteProps,
  cursorDataProps,
  feedTypeProps,
  indexProps
} from '../../props/feed-prop-types';
import FeedEntityContext from '../../state/FeedEntityContext';
import { init, reducer } from '../../state/feed-entity-reducer';
import { buildInitialState } from '../../state/feed-entity-init-state';
import AthleteJoinEntry from '../shared/AthleteJoinEntry';

const i18nPrefix = 'components.feed.clubs';

const ClubJoin = ({
  viewingAthlete,
  clubId,
  page,
  entity,
  feedType,
  index,
  cursorData,
  rowData,
  athleteProfileId
}) => {
  const { trackAnalytics, intersectionObserverRef } = useFeedAnalytics({
    page,
    entity,
    entityId: rowData.entity_id,
    athleteProfileId,
    feedType,
    cursorData,
    viewingAthleteId: viewingAthlete.id,
    clubId
  });

  const [entityState] = useReducer(
    reducer,
    buildInitialState({
      trackAnalytics
    }),
    init
  );

  const providerState = {
    entityState
  };

  const clubIcon = () => {
    switch (rowData.club_type) {
      case 'cycling':
        return <SportsBikeNormalXsmall />;
      case 'running':
        return <SportsRunNormalXsmall />;
      case 'swimming':
        return <SportsWaterNormalXsmall />;
      default:
        return null;
    }
  };

  const avatar = (
    <Avatar
      className={styles.avatar}
      data-testid="owner-avatar"
      src={rowData.club_avatar}
      type="club"
      name={rowData.club_name}
      href={rowData.club_url}
      badge={rowData.club_verified ? 'verified' : ''}
      onClick={() => {
        entityState.trackAnalytics({
          action: 'click',
          element: 'avatar'
        });
      }}
    />
  );

  const bodyProps = {
    entity,
    title: rowData.title,
    badge: avatar,
    entityName: rowData.club_name,
    entityUrl: rowData.club_url,
    entityIcon: clubIcon(),
    entityMemberCount: rowData.member_count,
    isMember: rowData.membership === 'member',
    secondaryText: rowData.location,
    intersectionObserverRef,
    index,
    cursorData,
    entityId: rowData.entity_id,
    labels: {
      join: I18n.t(`${i18nPrefix}.view`),
      view: I18n.t(`${i18nPrefix}.join`),
      joined: I18n.t(`${i18nPrefix}.joined`),
      memberCountKey: `${i18nPrefix}.members`,
      error: I18n.t(`${i18nPrefix}.error`)
    }
  };

  return (
    <FeedEntityContext.Provider value={providerState}>
      <AthleteJoinEntry {...bodyProps} />
    </FeedEntityContext.Provider>
  );
};

ClubJoin.propTypes = {
  // feed entity could be Activity, group_activity, Post etc
  entity: PropTypes.oneOf(['Club']).isRequired,
  viewingAthlete: viewingAthleteProps.isRequired,
  clubId: PropTypes.string.isRequired,
  page: pageProps.isRequired,
  cursorData: cursorDataProps.isRequired,
  feedType: feedTypeProps.isRequired,
  // range of number 0...19 representing rows returned by the server per feed load
  index: indexProps.isRequired,
  rowData: PropTypes.shape({
    title: PropTypes.string.isRequired,
    club_avatar: PropTypes.string.isRequired,
    club_verified: PropTypes.bool.isRequired,
    club_name: PropTypes.string.isRequired,
    entity_id: PropTypes.number.isRequired,
    entity_owner: PropTypes.string.isRequired,
    club_url: PropTypes.string.isRequired,
    location: PropTypes.string.isRequired,
    member_count: PropTypes.number.isRequired,
    club_type: PropTypes.string.isRequired,
    membership: PropTypes.string
  }).isRequired,
  athleteProfileId: PropTypes.string.isRequired
};

export default ClubJoin;
